import { useFrontDataStore } from '../stores/front';
import { useAdminDataStore } from '../stores/admin';
import api from './api'
import LZString from 'lz-string';
import moment from 'moment';
import replaceHtml from '../misc/replaceHtml';
import normalizeMenu from '../misc/normalizeMenu';
import timeoutAsync from '../misc/timeoutAsync';
import idbCache from './idbCache';


function normalizePage(page) {
  return {
    ...page,
    descriptionGeneratedHtml: replaceHtml(page.description, page.descriptionHtml),
    descriptionMobileGeneratedHtml: replaceHtml(page.description, page.descriptionMobileHtml || page.descriptionHtml),
  };
}
function normalizeMediaObject(m) {
  return {
    ...m,
    absoluteUri: `${process.env.VUE_APP_APIREMOTEROOTURL}${m.contentUrl}`,
  };
}


const get = {
  ApiBaseUrl: `${process.env.VUE_APP_APIROOTURL}`,
  
  IDB_TABLE_FOOD_RECIPES: 'food_recipes',
  IDB_TABLE_FOOD_INGREDIENTS: 'food_ingredients',
  IDB_TABLE_FOOD_MENUS: 'food_menus',
  getIdbName(tableName) {
    return idbCache.getDbName(tableName);
  },
  
  getHomeMenus(forceRefresh = false) {
    var frontStore = useFrontDataStore();
    var adminStore = useAdminDataStore();
    if(forceRefresh || frontStore.homeMenus.length == 0)  {
      api
        .listMenus("isDefaultMenu=true")
        .then((response) => {
          const menus = response['member'];
          const richMenus = menus
            .map(menu => normalizeMenu(menu, true, adminStore.foodRecipes))
            .sort((m1, m2) => {
              if(m1.canOrder != m2.canOrder) {
                return m1.canOrder ? -1 : 1;
              }
              if(m1.forDate == m2.forDate) {
                return 0;
              }
              return m1.forDate > m2.forDate ? 1 : -1;
            });
          frontStore.setHomeMenus(richMenus);
        });
    }
  },
  
  getMenu(id, forceRefresh = false) {
    var frontStore = useFrontDataStore();
    var adminStore = useAdminDataStore();
    if(forceRefresh || !frontStore.menu[id] || Object.keys(frontStore.menu[id]) == 0)  {
      return api
        .getMenu(id)
        .then((response) => {
          const menu = response;
          frontStore.setMenu(normalizeMenu(menu, true, adminStore.foodRecipes));
        });
    }
  },
  async getAdminMenu(id, forceRefresh = false) {
    var frontStore = useFrontDataStore();
    var adminStore = useAdminDataStore();
    
    await this.listAllFoodRecipes();

    const normalizeBeforeReturn = (menu) => {      
      return normalizeMenu(menu, false, adminStore.foodRecipes);
    }

    if(forceRefresh || !frontStore.adminMenu[id] || Object.keys(frontStore.adminMenu[id]) == 0)  {
      const isLoading = frontStore.adminMenuLoading[id];
      if(isLoading && !forceRefresh) {
        return;
      }
      frontStore.setAdminMenuLoading(id, true);

      const cachedItem = await idbCache.get(this.IDB_TABLE_FOOD_MENUS, id);
      if(cachedItem) {
        try {
          const decoded = cachedItem.data;
          if(decoded) {
            const decodedNormed = normalizeBeforeReturn(decoded);
            frontStore.setAdminMenu(decodedNormed);
          }
        }
        catch(e) {
          console.error(e);
        }
      }

      api
        .getAdminMenu(id)
        .then((response) => {
          const menu = response;          
          frontStore.setAdminMenu(normalizeMenu(menu, false, adminStore.foodRecipes));
          frontStore.setAdminMenuLoading(id, false);

          const plain = JSON.parse(JSON.stringify(menu));
          idbCache.put(this.IDB_TABLE_FOOD_MENUS, { id: plain.id, modifiedAt: plain.modifiedAt, data: plain });
        });
    }
  },

  // Charge le menu complet (avec les foodMenuItemOptionIngredientPrices), en s'appuyant sur le cache IndexedDB.
  async loadFullMenu(id) {
    const cachedItem = await idbCache.get(this.IDB_TABLE_FOOD_MENUS, id);
    // Le cache food_menus peut contenir une version allégée (via listAdminMenus) sans foodMenuItems.
    const hasDetail = cachedItem && cachedItem.data
      && Array.isArray(cachedItem.data.foodMenuItems) && cachedItem.data.foodMenuItems.length > 0;
    if(hasDetail) {
      return cachedItem.data;
    }
    const menu = await api.getAdminMenu(id);
    if(menu && menu.id) {
      const plain = JSON.parse(JSON.stringify(menu));
      await idbCache.put(this.IDB_TABLE_FOOD_MENUS, { id: plain.id, modifiedAt: plain.modifiedAt, data: plain });
    }
    return menu;
  },

  // Charge le détail complet de tous les menus, avec un toast de progression.
  async loadAllFullMenus() {
    const frontStore = useFrontDataStore();
    const adminStore = useAdminDataStore();
    await this.listAdminMenus();
    const menuIds = frontStore.adminMenus.map((m) => m.id).filter((id) => id != null);
    adminStore.startMenuPriceHistoryLoad(menuIds.length);
    try {
      await Promise.all(menuIds.map((id) =>
        this.loadFullMenu(id)
          .catch(() => null)
          .finally(() => adminStore.incrementMenuPriceHistoryProcessedCount())
      ));
    } finally {
      adminStore.resetMenuPriceHistoryLoad();
    }
  },

  async listIngredientPriceHistory(ingredientId) {
    const adminStore = useAdminDataStore();
    await this.listAllFoodRecipes();

    const targetId = Number(ingredientId);

    const extractIngredientId = (fi) => {
      if(!fi) {
        return null;
      }
      if(typeof fi === 'string') {
        const m = fi.match(/(\d+)$/);
        return m ? Number(m[1]) : null;
      }
      return fi.id != null ? Number(fi.id) : null;
    };

    // Collecte des ids de menus où figure une recette utilisant l'ingrédient
    const menuIds = new Set();
    adminStore.foodRecipes.forEach((recipe) => {
      if(recipe.deletedAt) {
        return;
      }
      const uses = (recipe.foodRecipeIngredients || []).some((fri) => {
        if(fri.deletedAt) {
          return false;
        }
        return extractIngredientId(fri.foodIngredient) == targetId;
      });
      if(!uses) {
        return;
      }
      (recipe.foodMenuItemOptions || []).forEach((fmio) => {
        const menu = fmio.foodMenuItem && fmio.foodMenuItem.foodMenu;
        if(menu && menu.id != null) {
          menuIds.add(Number(menu.id));
        }
      });
    });

    const recipeNameById = {};
    adminStore.foodRecipes.forEach((r) => { recipeNameById[r.id] = r.name; });

    const points = [];
    const menuIdList = [...menuIds];
    adminStore.startMenuPriceHistoryLoad(menuIdList.length);
    let menus;
    try {
      menus = await Promise.all(menuIdList.map((mid) =>
        this.loadFullMenu(mid)
          .catch(() => null)
          .finally(() => adminStore.incrementMenuPriceHistoryProcessedCount())
      ));
    } finally {
      adminStore.resetMenuPriceHistoryLoad();
    }

    menus.forEach((menu) => {
      if(!menu) {
        return;
      }
      (menu.foodMenuItems || []).forEach((fmi) => {
        (fmi.foodMenuItemOptions || []).forEach((fmio) => {
          (fmio.foodMenuItemOptionIngredientPrices || []).forEach((price) => {
            if(extractIngredientId(price.foodIngredient) != targetId) {
              return;
            }
            const recipeId = fmio.foodRecipe
              ? (typeof fmio.foodRecipe === 'string'
                  ? Number((fmio.foodRecipe.match(/(\d+)$/) || [])[1])
                  : fmio.foodRecipe.id)
              : null;
            points.push({
              id: price.id,
              unitPriceInCts: price.unitPriceInCts,
              unitQuantity: price.unitQuantity,
              menuId: menu.id,
              menuLabel: menu.label,
              menuDate: menu.menuDate,
              forDate: menu.forDate,
              recipeId: recipeId,
              recipeName: (recipeId != null ? recipeNameById[recipeId] : null) || (fmio.foodRecipe && fmio.foodRecipe.name) || null,
            });
          });
        });
      });
    });

    points.sort((a, b) => moment(a.forDate) - moment(b.forDate));
    return points;
  },
  
  getOrder(orderId, shortCode, forceRefresh = false) {
    var frontStore = useFrontDataStore();
    if(forceRefresh || frontStore.order.id != orderId)  {
      frontStore.setOrderIsLoading(true);
      api
        .getOrder(orderId, shortCode)
        .then((response) => {
          if(!response.error) {
            frontStore.setOrder({
              ...response,
              initialFoodMenuOrderItems: {...response.foodMenuOrderItems},
            });
          }else{
            frontStore.setOrderIsLoading(false);
            if(response.message) {
              frontStore.setOrderLoadError(response.message);
            }  
          }
        })
        .catch((e) => {
          frontStore.setOrderIsLoading(false);
          if(e.message) {
            frontStore.setOrderLoadError(e.message);
          }
        })
        ;
    }
  },
  async listMenus(forceRefresh = false) {
    var frontStore = useFrontDataStore();
    var adminStore = useAdminDataStore();
    if(forceRefresh || frontStore.menus.length == 0)  {
      frontStore.setMenusAreLoading(true);
      
      let allMenus = [];
      let page = 1;
      const itemsPerPage = 50;
      let hasMore = true;
      while(hasMore) {

        var response = await api
        .listMenus("itemsPerPage="+itemsPerPage+"&page="+page);
          //.then((response) => {
          const menus = response['member'];
          hasMore = menus.length == itemsPerPage;

          allMenus = [
            ...allMenus, 
            ...(menus.map((menu) => (normalizeMenu(menu, true, adminStore.foodRecipes))))
          ];

          //we add some order to sub properties here
          const menusOrdered = allMenus
          .sort((a, b) => ((a.forDate < b.forDate) ? 1 : -1))
          ;

          frontStore.setMenus(menusOrdered);
          page++;
          //frontStore.setMenus(menusOrdered);
        ///});
          }
    }
  },
  async listAdminMenus(forceRefresh = false) {
    var adminStore = useAdminDataStore();
    const normalizeBeforeReturn = (menus) => {
      return menus.map((menu) => (normalizeMenu(menu, true, adminStore.foodRecipes)))
      .sort((a, b) => ((a.forDate < b.forDate) ? 1 : -1));
    }

    let firstLastModified = '';

    var frontStore = useFrontDataStore();
    let decodedNormed = null;
    if(forceRefresh || frontStore.adminMenus.length == 0)  {
      frontStore.setAdminMenusAreLoading(true);

      const cachedItems = await idbCache.getAll(this.IDB_TABLE_FOOD_MENUS);
      if(cachedItems && cachedItems.length > 0) {
        try {
          cachedItems.sort((a, b) => (a.modifiedAt > b.modifiedAt ? -1 : 1));
          const decoded = cachedItems.map(item => item.data);
          if(decoded.length >= 1) {
            firstLastModified = cachedItems[0].modifiedAt;
          }
          decodedNormed = normalizeBeforeReturn(decoded);
        }
        catch(e) {
          console.error(e);
        }
      }

      let cachedCount = cachedItems ? cachedItems.length : 0;
      let apiTotalItems = null;

      {
        const responseToCheck = await api.listAdminMenus("itemsPerPage=1&page=1");
        const membersToCheck = responseToCheck['member'];
        apiTotalItems = responseToCheck['totalItems'];
        if(membersToCheck.length >= 1) {
          const recentFirstLastModified = membersToCheck[0].modifiedAt;
          if(recentFirstLastModified == firstLastModified && apiTotalItems == cachedCount) {
            // tout est OK, on peut charger depuis le store
            frontStore.setAdminMenus(decodedNormed);
            return;
          }
        }
      }

      const canStopEarly = () => apiTotalItems == cachedCount;

      let allMenus = [];
      let allMenuUntoucheds = [];
      // on repars de notre liste et on va "patcher"
      if(decodedNormed) {
        allMenus = decodedNormed;
      }
      let page = 1;
      const itemsPerPage = 50;
      let hasMore = true;
      let storeUpdated = false;
      let stoppedEarly = false;
      while(hasMore) {
        const response = await api
          .listAdminMenus("itemsPerPage="+itemsPerPage+"&page="+page);
            const menus = response['member'];
            allMenuUntoucheds.push(...menus);
            const lastMenu = menus[menus.length - 1];
            
            hasMore = menus.length == itemsPerPage;

            const menusNormed = normalizeBeforeReturn(menus);
            menusNormed.forEach((menu) => {
              const ix = allMenus.findIndex(lm => lm.id == menu.id);
              if(ix == -1) {
                allMenus.push(menu);
                cachedCount++;
              }else{
                allMenus[ix] = menu;
              }
            });

            if(canStopEarly() && firstLastModified && moment(firstLastModified) >= moment(lastMenu.modifiedAt)) {
              stoppedEarly = true;
              if(!storeUpdated) {
                storeUpdated = true;
                allMenus.sort((a, b) => ((a.forDate < b.forDate) ? 1 : -1));
                frontStore.setAdminMenus([...allMenus]);
              }
              break;
            }
            
            page++;
      }

      if(!storeUpdated) {
        allMenus.sort((a, b) => ((a.forDate < b.forDate) ? 1 : -1));
        frontStore.setAdminMenus([...allMenus]);
      }

      if(stoppedEarly) {
        idbCache.putAll(this.IDB_TABLE_FOOD_MENUS, allMenuUntoucheds.map(r => {
          const plain = JSON.parse(JSON.stringify(r));
          return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
        }));
      } else {
        idbCache.replaceAll(this.IDB_TABLE_FOOD_MENUS, allMenuUntoucheds.map(r => {
          const plain = JSON.parse(JSON.stringify(r));
          return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
        }));
      }
    }
  },
  listPages(forceRefresh = false) {
    var frontStore = useFrontDataStore();
    if(forceRefresh || Object.keys(frontStore.pages).length == 0)  {
      frontStore.setPagesAreLoading(true);
      api
        .listPages("")
        .then((response) => {
          const pages = response['member'];

          //we add some order to sub properties here
          const pagesOrdered = pages
          .sort((a, b) => ((a.shortname.localeCompare(b.shortname))))
          .map((page) => (normalizePage(page)))
          ;
          const pagesAsHash = pagesOrdered.reduce((acc, item) => ({
            [item.shortname]: item,
            ...acc,
          }), {});
          frontStore.setPages(pagesAsHash);
        });
    }
  },
  async listMediaObjects(forceRefresh = false) {
    var frontStore = useFrontDataStore();
    if(forceRefresh || Object.keys(frontStore.mediaObjects).length == 0)  {
      frontStore.setMediaObjectsAreLoading(true);
      let ended = false;
      let page = 1;
      let images = [];
      while(!ended && page < 100) {
        const response = await api
          .listMediaObjects("", page);
          images = [...images, ...(response['member'])];
          //const response = await api.listNewsletterRecipients(page);
        
          ended = !(response['view'] && response['view']['next']);
          page++;
      }
      const mediaObjectsOrdered = images
        .sort((a, b) => (b.id - a.id))
        .map((m) => (normalizeMediaObject(m)))
        ;
      frontStore.setMediaObjects(mediaObjectsOrdered);
    }
  },
  listMailTemplates(forceRefresh = false) {
    var frontStore = useFrontDataStore();
    if(forceRefresh || Object.keys(frontStore.mailTemplates).length == 0)  {
      frontStore.setMailTemplatesAreLoading(true);
      api
        .listMailTemplates("")
        .then((response) => {
          const mails = response['member'];

          //we add some order to sub properties here
          const mailsOrdered = mails
          .sort((a, b) => ((a.shortname.localeCompare(b.shortname))))
          //.map((page) => (normalizePage(page)))
          ;
          const mailsAsHash = mailsOrdered.reduce((acc, item) => ({
            [item.shortname]: item,
            ...acc,
          }), {});
          frontStore.setMailTemplates(mailsAsHash);
        });
    }
  },
  listMenuCustomers(forceRefresh = false) {
    var adminStore = useAdminDataStore();
    if(forceRefresh || Object.keys(adminStore.menuCustomers).length == 0)  {
      adminStore.setMenuCustomersAreLoading(true);
      api
        .listMenuCustomers()
        .then((response) => {
          //const customers = response['member'];
          const customers = response;

          //we add some order to sub properties here
          const customersOrdered = customers
          .sort((a, b) => ((a.lastName.localeCompare(b.lastName))))
          //.map((page) => (normalizePage(page)))
          ;
          
          adminStore.setMenuCustomers(customersOrdered);
        });
    }
  },
  
  async listAllNewsletterRecipients(forceRefresh = false) {
    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.newsletterRecipients.length == 0)  {
      adminStore.setNewsletterRecipientsAreLoading(true);
      let ended = false;
      let page = 1;
      let recipients = [];
      while(!ended && page < 100) {
        const response = await api.listNewsletterRecipients(page);
        recipients = [...recipients, ...(response['member'])];
        ended = !(response['view'] && response['view']['next']);
        page++;
      }
      
      adminStore.setNewsletterRecipients(recipients.sort((a, b) => a.email.localeCompare(b.email)));
    }
  },
  async listAllFoodMenuClients(forceRefresh = false) {
    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.foodMenuClients.length == 0)  { 
      adminStore.setFoodMenuClientsAreLoading(true);
      let ended = false;
      let page = 1;
      let clients = [];
      while(!ended && page < 100) {
        const response = await api.listFoodMenuClients(page);
        clients = [...clients, ...(response['member'])];
        ended = !(response['view'] && response['view']['next']);
        page++;
      }
      
      adminStore.setFoodMenuClients(clients.sort((a, b) => a.forEmail && b.forEmail ? a.forEmail.localeCompare(b.forEmail) : 0));
    }
  },
  setIngredientsToStoreCache(/* ings */) {
    return;
    /*
    const storageKey = process.env.VUE_APP_APIROOTURL+'listAllFoodIngredients';
    const cachedValue = window.localStorage.getItem(storageKey);
      if(cachedValue) {
        try {
          const decoded = JSON.parse(LZString.decompress(cachedValue));
          if(decoded) {
            // 1. Récupère les IDs des nouveaux éléments
            const ingIds = new Set(ings.map(ing => ing.id));

            // 2. Filtre decoded pour retirer les doublons
            const cleanedDecoded = decoded.filter(item => !ingIds.has(item.id));

            // 3. Fusionne
            const merged = [...cleanedDecoded, ...ings].sort((a, b) => {
              return moment(b.modifiedAt) - moment(a.modifiedAt);
              
            });
            window.localStorage.setItem(storageKey, LZString.compress(JSON.stringify(merged)));
          }
        }
        catch(e) {
          console.error(e);
        }
      }
        */
  },
  
  setRecipesToStoreCache(/* ings */) {
    return ;
    /*
    const storageKey = process.env.VUE_APP_APIROOTURL+'listAllFoodRecipesInSameTime';
    const cachedValue = window.localStorage.getItem(storageKey);
      if(cachedValue) {
        try {
          const decoded = JSON.parse(LZString.decompress(cachedValue));
          if(decoded) {
            // 1. Récupère les IDs des nouveaux éléments
            const ingIds = new Set(ings.map(ing => ing.id));

            // 2. Filtre decoded pour retirer les doublons
            const cleanedDecoded = decoded.filter(item => !ingIds.has(item.id));

            // 3. Fusionne
            const merged = [...cleanedDecoded, ...ings].sort((a, b) => {
              return moment(b.modifiedAt) - moment(a.modifiedAt);
              
            });
            window.localStorage.setItem(storageKey, LZString.compress(JSON.stringify(merged)));
          }
        }
        catch(e) {
          console.error(e);
        }
      }
        */
  },
  
  async refreshRecentFoodIngredients(nb = 5) {
    var adminStore = useAdminDataStore();
    const itemsPerPage = nb;
    const response = await api.listFoodIngredients(1, "itemsPerPage="+itemsPerPage);
    const members = response['member'];
    for(let i = 0; i < members.length; i++) {
      var foodIngredient = members[i];
      
      adminStore.refreshFoodIngredient(this.formatIngredientsBeforeReturn([foodIngredient])[0]);
    }
    idbCache.putAll(this.IDB_TABLE_FOOD_INGREDIENTS, members.map(r => {
      const plain = JSON.parse(JSON.stringify(r));
      return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
    }));
  },
  async refreshAFoodIngredient(id) {
    var adminStore = useAdminDataStore();
    const response = await api.listFoodIngredients(1, "id="+id+"&itemsPerPage=1");
    const members = response['member'];
    for(let i = 0; i < members.length; i++) {
      var foodIngredient = members[i];
      adminStore.refreshFoodIngredient(this.formatIngredientsBeforeReturn([foodIngredient])[0]);
    }
    idbCache.putAll(this.IDB_TABLE_FOOD_INGREDIENTS, members.map(r => {
      const plain = JSON.parse(JSON.stringify(r));
      return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
    }));
  },
  async refreshAllFoodIngredients() {
    return this.listAllFoodIngredients(true, false);
  },
  formatIngredientsBeforeReturn(ings) {
    return ings.map(i => {
      let total = 0;
      if(i.allGluten) {
        total++;
      }
      if(i.allCrustrace) {
        total++;
      }
      if(i.allOeuf) {
        total++;
      }
      if(i.allArachide) {
        total++;
      }
      if(i.allPoisson) {
        total++;
      }
      if(i.allSoja) {
        total++;
      }
      if(i.allLait) {
        total++;
      }
      if(i.allFruitCoques) {
        total++;
      }
      if(i.allCeleri) {
        total++;
      }
      if(i.allMoutarde) {
        total++;
      }
      if(i.allSesame) {
        total++;
      }
      if(i.allSulfite) {
        total++;
      }
      if(i.allLupin) {
        total++;
      }
      
      return {
        ...i, 
        allCount: total
      };

    })
    .sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
  },
  async listAllFoodIngredients(forceRefresh = false, setLoading = true) {

    let firstLastModified = '';
    let decodedNormed = null;

    const formatBeforeReturn = (ings) => {
      return this.formatIngredientsBeforeReturn(ings);
    };
    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.foodIngredients.length == 0)  {
      if(adminStore.foodIngredientsAreLoading) {
        while(adminStore.foodIngredientsAreLoading) {
          await timeoutAsync(200);
        }
        return;
      }
      if(setLoading) {
        adminStore.setFoodIngredientsAreLoading(true);
      }
      
      const cachedItems = await idbCache.getAll(this.IDB_TABLE_FOOD_INGREDIENTS);
      if(cachedItems && cachedItems.length > 0) {
        try {
          cachedItems.sort((a, b) => (a.modifiedAt > b.modifiedAt ? -1 : 1));
          const decoded = cachedItems.map(item => item.data);
          if(decoded.length >= 1) {
            firstLastModified = cachedItems[0].modifiedAt;
          }
          decodedNormed = formatBeforeReturn(decoded);
        }
        catch(e) {
          console.error(e);
        }
      }

      let cachedCount = cachedItems ? cachedItems.length : 0;
      let apiTotalItems = null;

      {
        const responseToCheck = await api.listFoodIngredients(1, "itemsPerPage=1");
        const membersToCheck = responseToCheck['member'];
        apiTotalItems = responseToCheck['totalItems'];
        
        if(membersToCheck.length >= 1) {
          const recentFirstLastModified = membersToCheck[0].modifiedAt;
          if(recentFirstLastModified == firstLastModified && apiTotalItems == cachedCount) {
            adminStore.setFoodIngredients(decodedNormed);
            return;
          }
        }
      }

      const canStopEarly = () => apiTotalItems == cachedCount;

      let page = 1;
      const itemsPerPage = 50;
      let ingredients = [];
      if(decodedNormed) {
        ingredients = decodedNormed;
      }
      let ingredientUntoucheds = [];
      let storeUpdated = false;
      let stoppedEarly = false;

      let hasMore = true;
      while(hasMore) {
        const response = await api.listFoodIngredients(page, "itemsPerPage="+itemsPerPage);
        const members = response['member'];
        page++;


        if(members.length == 0) {
          hasMore = false;
          break;
        }

        const lastIngredient = members[members.length - 1];

        hasMore = members.length == itemsPerPage;
        
        ingredientUntoucheds.push(...members);
        const membersNormed = formatBeforeReturn(members);
        membersNormed.forEach((ingredient) => {
          const ix = ingredients.findIndex(i => i.id == ingredient.id);
          if(ix == -1) {
            ingredients.push(ingredient);
            cachedCount++;
          }else{
            ingredients[ix] = ingredient;
          }
        });

        if(canStopEarly() && firstLastModified && moment(firstLastModified) >= moment(lastIngredient.modifiedAt)) {
          stoppedEarly = true;
          if(!storeUpdated) {
            storeUpdated = true;
            ingredients.sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
            adminStore.setFoodIngredients(ingredients);
          }
          break;
        }
      }

      if(!storeUpdated) {
        ingredients.sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
        adminStore.setFoodIngredients(ingredients);
      }

      if(stoppedEarly) {
        idbCache.putAll(this.IDB_TABLE_FOOD_INGREDIENTS, ingredientUntoucheds.map(r => {
          const plain = JSON.parse(JSON.stringify(r));
          return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
        }));
      } else {
        idbCache.replaceAll(this.IDB_TABLE_FOOD_INGREDIENTS, ingredientUntoucheds.map(r => {
          const plain = JSON.parse(JSON.stringify(r));
          return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
        }));
      }
    }
  },
  
  async listAllFoodMenuItems(forceRefresh = false) {
    const formatForResponse = (fmis) => {
      return fmis.sort((a, b) => a.label.localeCompare(b.label));
    };
    let firstLastModified = '-1';
    var adminStore = useAdminDataStore();
    const storageKey = process.env.VUE_APP_APIROOTURL+'allFoodMenuItemsSorted';
    if(forceRefresh || adminStore.foodMenuItems.length == 0)  {
      if(adminStore.foodMenuItemsAreLoading) {
        return;
      }
      adminStore.setFoodMenuItemsAreLoading(true);

      const cachedValue = window.localStorage.getItem(storageKey);
      if(cachedValue) {
        try {
          const decoded = JSON.parse(LZString.decompress(cachedValue));
          if(decoded) {
            if(decoded.length >= 1) {
              firstLastModified = decoded[0].modifiedAt;
            }

            adminStore.setFoodMenuItems(formatForResponse(decoded));
          }
        }
        catch(e) {
          console.error(e);
        }
      }      

      {
        const responseToCheck = await api.listFoodMenuItems(1, "itemsPerPage=1");

        const membersToCheck = responseToCheck['member'];
        if(page == 1 && membersToCheck.length >= 1) {
          const recentFirstLastModified = membersToCheck[0].modifiedAt;
          if(recentFirstLastModified && recentFirstLastModified == firstLastModified) {
            return;
          }
        }
      }

      let ended = false;
      let page = 1;
      let foodMenuItems = [];
      while(!ended && page < 100) {
        const response = await api.listFoodMenuItems(page);

        const members = response['member'];
        if(page == 1 && members.length >= 1) {
          const recentFirstLastModified = members[0].modifiedAt;
          if(recentFirstLastModified && recentFirstLastModified == firstLastModified) {
            return;
          }
        }


        foodMenuItems.push(...members);
        ended = !(response['view'] && response['view']['next']);
        page++;
      }
      
      adminStore.setFoodMenuItems(formatForResponse(foodMenuItems));
      setTimeout(() => {
        window.localStorage.setItem(storageKey, LZString.compress(JSON.stringify(foodMenuItems)));
      }, 500);
    }
  },

  
  async listAllFoodMenuOrders(forceRefresh = false) {
    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.foodMenuOrders.length == 0)  {
      if(adminStore.foodMenuOrdersAreLoading) {
        return;
      }
      adminStore.setFoodMenuOrdersAreLoading(true);
      let ended = false;
      let page = 1;
      let foodMenuOrders = [];
      while(!ended && page < 100) {
        const response = await api.listFoodMenuOrders(page);
        foodMenuOrders = [...foodMenuOrders, ...(response['member'])];
        ended = !(response['view'] && response['view']['next']);
        page++;
      }
      adminStore.setFoodMenuOrders(foodMenuOrders);
    }
  },

  async listAllOrderDiscountRules(forceRefresh = false) {
    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.orderDiscountRules.length == 0)  {
      if(adminStore.orderDiscountRulesAreLoading) {
        return;
      }
      adminStore.setOrderDiscountRulesAreLoading(true);
      let ended = false;
      let page = 1;
      let discounts = [];
      while(!ended && page < 100) {
        const response = await api.listOrderDiscountRules(page);
        discounts = [...discounts, ...(response['member'])];
        ended = !(response['view'] && response['view']['next']);
        page++;
      }
      
      adminStore.setOrderDiscountRules(discounts.sort((a, b) => `${a.forEmail}`.localeCompare(`${b.forEmail}`)));
    }
  },
  async refreshRecentFoodRecipes(nb = 5) {
    //return this.listAllFoodRecipes(true, false);
    var adminStore = useAdminDataStore();
    const itemsPerPage = nb;
    const response = await api.listFoodRecipes(1, "itemsPerPage="+itemsPerPage);
    const rawMembers = response['member'];
    let members = rawMembers.map((recipe)=> {
      return this.fillFoodRecipeIngredient(recipe, adminStore);
    });
    for(let i = 0; i < members.length; i++) {
      var foodRecipe = members[i];
      adminStore.refreshFoodRecipe(foodRecipe);
    }
    idbCache.putAll(this.IDB_TABLE_FOOD_RECIPES, rawMembers.map(r => {
      const plain = JSON.parse(JSON.stringify(r));
      return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
    }));
  },
  async refreshAFoodRecipe(id) {
    //return this.listAllFoodRecipes(true, false);
    var adminStore = useAdminDataStore();
    const response = await api.listFoodRecipes(1, "id="+id+"&itemsPerPage=1");
    const rawMembers = response['member'];
    for(let i = 0; i < rawMembers.length; i++) {
      let foodRecipe = rawMembers[i];
      foodRecipe = this.fillFoodRecipeIngredient(foodRecipe, adminStore);
      adminStore.refreshFoodRecipe(foodRecipe);
    }
    idbCache.putAll(this.IDB_TABLE_FOOD_RECIPES, rawMembers.map(r => {
      const plain = JSON.parse(JSON.stringify(r));
      return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
    }));
  },
  async removeFoodRecipeFromCache(id) {
    const adminStore = useAdminDataStore();
    adminStore.deleteAFoodRecipe(id);
    await idbCache.delete(this.IDB_TABLE_FOOD_RECIPES, id);
    const storageKey = process.env.VUE_APP_APIROOTURL + 'getFoodRecipe-' + id;
    window.localStorage.removeItem(storageKey);
  },
  async removeFoodIngredientFromCache(id) {
    const adminStore = useAdminDataStore();
    adminStore.deleteAFoodIngredient(id);
    await idbCache.delete(this.IDB_TABLE_FOOD_INGREDIENTS, id);
  },
  refreshAllFoodRecipes() {
    return this.listAllFoodRecipes(true, false);
  },
  async listAllFoodRecipes(forceRefresh = false, setLoading = true) {
    return await this.listAllFoodRecipesInSameTime(forceRefresh, setLoading);
    /*
    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.foodRecipes.length == 0)  {
      if(adminStore.foodRecipesAreLoading) {
        return;
      }
      if(setLoading) {
        adminStore.setFoodRecipesAreLoading(true);
      }
      let ended = false;
      let page = 1;
      const itemsPerPage = 50;
      let recipes = [];
      while(!ended && page < 100) {
        const response = await api.listFoodRecipes(page, "itemsPerPage="+itemsPerPage);
        recipes = [...recipes, ...(response['member'])];
        ended = !(response['view'] && response['view']['next']);
        page++;
      }
      
      adminStore.setFoodRecipes(recipes.sort((a, b) => a.name.localeCompare(b.name)));
    }
    */
  },
  async listAllFoodRecipesInSameTime(forceRefresh = false, setLoading = true) {
    const formatForResponse = (foodRecipes) => {
      return foodRecipes
        .map((recipe)=> {
          return this.fillFoodRecipeIngredient(recipe, adminStore);
        })
        .sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
    };
    
    let firstLastModified = '';
    let decodedNormed = null;

    var adminStore = useAdminDataStore();
    if(forceRefresh || adminStore.foodRecipes.length == 0)  {
      if(adminStore.foodRecipesAreLoading) {
        return;
      }
      if(setLoading) {
        adminStore.setFoodRecipesAreLoading(true);
      }

      const cachedItems = await idbCache.getAll(this.IDB_TABLE_FOOD_RECIPES);
      if(cachedItems && cachedItems.length > 0) {
        try {
          cachedItems.sort((a, b) => (a.modifiedAt > b.modifiedAt ? -1 : 1));
          const decoded = cachedItems.map(item => item.data);
          if(decoded.length >= 1) {
            firstLastModified = cachedItems[0].modifiedAt;
          }

          await this.listAllFoodIngredients();
          decodedNormed = formatForResponse(decoded);
        }
        catch(e) {
          console.error(e);
        }
      }

      let cachedCount = cachedItems ? cachedItems.length : 0;
      let apiTotalItems = null;

      {        
        const responseToCheck = await api.listFoodRecipes(1, "itemsPerPage=1");
        const membersToCheck = responseToCheck['member'];
        apiTotalItems = responseToCheck['totalItems'];
        
        if(membersToCheck.length >= 1) {
          const recentFirstLastModified = membersToCheck[0].modifiedAt;
          if(recentFirstLastModified == firstLastModified && apiTotalItems == cachedCount) {
            adminStore.setFoodRecipes(decodedNormed);
            return;
          }
        }
      }

      const canStopEarly = () => apiTotalItems == cachedCount;

      let page = 1;
      const itemsPerPage = 50;
      let recipes = [];

      if(decodedNormed) {
        recipes = decodedNormed;
      }
      let recipeUntoucheds = [];
      let storeUpdated = false;
      let stoppedEarly = false;
      
      let hasMore = true;
      while(hasMore) {
        const response = await api.listFoodRecipes(page, "itemsPerPage="+itemsPerPage);
        const members = response['member'];
        page++;

        if(members.length == 0) {
          hasMore = false;
          break;
        }

        const lastRecipe = members[members.length - 1];


        
        hasMore = members.length == itemsPerPage;
        
        recipeUntoucheds.push(...members);
        const membersNormed = formatForResponse(members);
        membersNormed.forEach((recipe) => {
          const ix = recipes.findIndex(i => i.id == recipe.id);
          if(ix == -1) {
            recipes.push(recipe);
            cachedCount++;
          }else{
            recipes[ix] = recipe;
          }
        });

        if(canStopEarly() && firstLastModified && moment(firstLastModified) >= moment(lastRecipe.modifiedAt)) {
          // les données suivantes en cache sont suffisamment à jour
          stoppedEarly = true;
          if(!storeUpdated) {
            storeUpdated = true;
            recipes.sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
            adminStore.setFoodRecipes(recipes);
          }
          break;
        }
      }
      await this.listAllFoodIngredients();

      
      if(!storeUpdated) {
        recipes.sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
        adminStore.setFoodRecipes(recipes);
      }

      if(stoppedEarly) {
        // update incrémental : on upsert seulement les recettes récupérées
        idbCache.putAll(this.IDB_TABLE_FOOD_RECIPES, recipeUntoucheds.map(r => {
          const plain = JSON.parse(JSON.stringify(r));
          return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
        }));
      } else {
        // tout a été récupéré : on remplace entièrement le cache
        idbCache.replaceAll(this.IDB_TABLE_FOOD_RECIPES, recipeUntoucheds.map(r => {
          const plain = JSON.parse(JSON.stringify(r));
          return { id: plain.id, modifiedAt: plain.modifiedAt, data: plain };
        }));
      }
    }
  },
  fillFoodRecipeIngredient(recipe, adminStore) {
    if(!recipe) {
      return {};
    }
    recipe.foodRecipeIngredients = (recipe.foodRecipeIngredients || []).map((fri) => {
      if(typeof fri.foodIngredient == 'string') {
        var ingredient = adminStore.foodIngredients.find(fi => fi['@id'] == fri.foodIngredient);
        fri.foodIngredient = ingredient;
      }
      return fri;
    });
    return recipe;
  },
  getFoodRecipeNormalizeBeforeReturn(elm, adminStore) {
    var response = this.fillFoodRecipeIngredient(elm, adminStore);
      response.isLoading = false;
      return response;
  },
  
  async getFoodRecipe(id, forceRefresh = false, doAjaxCall = true) {
    var adminStore = useAdminDataStore();
    let e = adminStore.foodRecipes.find(r => r.id == id);
    if(e) {
      e = this.fillFoodRecipeIngredient(e, adminStore);
    }
    var isLoaded = e ? (typeof e.foodMenuItemOptions === 'object') : false;
    var isLoading = e ? e.isLoading : false;
    if((!isLoaded || forceRefresh) && !isLoading) {
      // prevent double loading
      if(e) {
        e.isLoading = true;
        adminStore.setFoodRecipe(id, e);
      }

      const cachedItem = await idbCache.get(this.IDB_TABLE_FOOD_RECIPES, id);
      if(cachedItem) {
        try {
          const decoded = cachedItem.data;
          if(decoded) {
            const decodedNormed = this.getFoodRecipeNormalizeBeforeReturn(decoded);
            adminStore.setFoodRecipe(id, decodedNormed);
            if(!doAjaxCall) {
              if(e) {
                e.isLoading = false;
                adminStore.setFoodRecipe(id, e);
              }
              return decodedNormed;
            }
          }
        }
        catch(e) {
          console.error(e);
        }
      }
      if(!doAjaxCall) {
        if(e) {
          e.isLoading = false;
          adminStore.setFoodRecipe(id, e);
          return e;
        } 
      }

      let response = await api.getFoodRecipe(id);
      response = this.fillFoodRecipeIngredient(response, adminStore);
      adminStore.setFoodRecipe(id, this.getFoodRecipeNormalizeBeforeReturn(response, adminStore));

      const plain = JSON.parse(JSON.stringify(response));
      idbCache.put(this.IDB_TABLE_FOOD_RECIPES, { id: plain.id, modifiedAt: plain.modifiedAt, data: plain });

      return response;
    }
    return e;
  },
  async getFoodMenuItem(id, forceRefresh = false, doAjaxCall = true) {
    const storageKey = process.env.VUE_APP_APIROOTURL+'getFoodMenuItem-'+id;

    var adminStore = useAdminDataStore();
    let e = adminStore.foodMenuItems.find(r => r.id == id);
    
    var isLoaded = e ? (typeof e.foodMenuItemOptions === 'object') : false;
    var isLoading = e ? e.isLoading : false;
    if((!isLoaded || forceRefresh) && !isLoading) {
      // prevent double loading
      if(e) {
        e.isLoading = true;
        adminStore.setFoodMenuItem(id, e);
      }

      const cachedValue = window.localStorage.getItem(storageKey);
      if(cachedValue) {
        try {
          const decoded = JSON.parse(LZString.decompress(cachedValue));
          if(decoded) {
            const decodedNormed = this.getFoodRecipeNormalizeBeforeReturn(decoded);
            adminStore.setFoodMenuItem(id, decodedNormed);
            if(!doAjaxCall) {
              return decodedNormed;
            }
          }
        }
        catch(e) {
          console.error(e);
        }
      }
      if(!doAjaxCall) {
        if(e) {
          e.isLoading = false;
          adminStore.setFoodMenuItem(id, e);
        } 
      }

      let response = await api.getFoodMenuItem(id);
      adminStore.setFoodMenuItem(id, response);

      setTimeout(() => {
        window.localStorage.setItem(storageKey, LZString.compress(JSON.stringify(response)));
      }, 500);

      return response;
    }
    return e;
  },
  
  async getFoodRecipeIngredient(id, forceRefresh = false, doAjaxCall = true) {
    const normalizeBeforeReturn = (elm) => {
      var response = elm;
      response.isLoading = false;
      return response;
    };

    const adminStore = useAdminDataStore();
    let e = adminStore.foodRecipeIngredients.find(r => r.id == id);
    var isLoaded = e ? (typeof e.foodRecipes === 'object') : false;
    var isLoading = e ? e.isLoading : false;
    if((!isLoaded || forceRefresh) && !isLoading) {
      // prevent double loading
      if(e) {
        e.isLoading = true;
        adminStore.setFoodRecipeIngredient(id, e);
      }

      const cachedItem = await idbCache.get(this.IDB_TABLE_FOOD_INGREDIENTS, id);
      if(cachedItem) {
        try {
          const decoded = cachedItem.data;
          if(decoded) {
            const decodedNormed = normalizeBeforeReturn(decoded);
            adminStore.setFoodRecipeIngredient(id, decodedNormed);
            if(!doAjaxCall) {
              return decodedNormed;
            }
          }
        }
        catch(e) {
          console.error(e);
        }
      }
      if(!doAjaxCall) {
        if(e) {
          e.isLoading = false;
          adminStore.setFoodRecipeIngredient(id, e);
        } 
      }

      let response = await api.getFoodRecipeIngredient(id);
      adminStore.setFoodRecipeIngredient(id, normalizeBeforeReturn(response, adminStore));

      const plain = JSON.parse(JSON.stringify(response));
      idbCache.put(this.IDB_TABLE_FOOD_INGREDIENTS, { id: plain.id, modifiedAt: plain.modifiedAt, data: plain });

      return response;
    }
    return e;
  },
  async refreshFoodRecipeRecipePrice(recipeId) {
    const adminStore = useAdminDataStore();
    const recipe = await this.getFoodRecipe(recipeId, true);
    const foodRecipeIngredients = recipe.foodRecipeIngredients;

    
    let priceInCts = 0.0;
    foodRecipeIngredients.forEach((data) => {
      if(!data.deleted) {
        const iPriceInCts = ((data.foodIngredient.unitPriceInCts * data.unitQuantity) / recipe.numberOfDish);
        priceInCts += iPriceInCts;
      }
    });

    priceInCts = Math.round(priceInCts);
    if(priceInCts != recipe.pricePerDishInCts) {
      
      const postFormData = {
        pricePerDishInCts: priceInCts,
        modifiedAt: new Date(),
      };

      var response = await api.editRecipe(recipeId, postFormData);
      const storageKey = process.env.VUE_APP_APIROOTURL+'getFoodRecipe-'+recipeId;

      await this.refreshAFoodRecipe(recipeId);

      response = this.fillFoodRecipeIngredient(response, adminStore);
      adminStore.setFoodRecipe(recipeId, this.getFoodRecipeNormalizeBeforeReturn(response, adminStore));
      window.localStorage.setItem(storageKey, LZString.compress(JSON.stringify(response)));

      // et on met à jour le store maintenant
      //await this.getFoodRecipe(recipeId, true);
    }    
  },

  initInactivityDetector() {
    if(!window.inactivityDetectorSetted) {      
      const INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes
      window.inactivityDetectorSetted = true;
      window.inactivityTimer = null;
      const resetInactivityTimer = () => {
        clearTimeout(window.inactivityTimer);
        window.inactivityTimer = setTimeout(() => {
          // ex: logout(), afficher une popup, etc.
          this.onIdleDetected();
        }, INACTIVITY_LIMIT);
      };
      ['mousemove', 'keydown', 'scroll', 'click'].forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
      });
    }
  },

  async onIdleDetected() {
    if(this.shouldRefreshAdminMenus) {
      this.shouldRefreshAdminMenus=false;
      await this.listAdminMenus(true);
      
    }
    if(this.shouldRefreshAdminRecipes) {
      this.shouldRefreshAdminRecipes = false;
      await this.listAllFoodRecipes(true, false);
    }
    if(this.shouldRefreshAdminIngredients) {
      this.shouldRefreshAdminIngredients = false;
      await this.listAllFoodIngredients(true, false);
    }
    if(this.shouldRefreshAdminMenuItems) {
      this.shouldRefreshAdminMenuItems = false;
      await this.listAllFoodMenuItems(true);
    }
  },

  refreshAdminMenusWhenIdle() {
    this.shouldRefreshAdminMenus = true;
  },
  refreshAdminMenuRecipesWhenIdle() {
    this.shouldRefreshAdminRecipes = true;
  },
  refreshAdminMenuIngredientsWhenIdle() {
    this.shouldRefreshAdminIngredients = true;
  },
  refreshAdminMenuItemsWhenIdle() {
    this.shouldRefreshAdminMenuItems = true;
  },
  async initAllAdminDatas() {
    await get.listAllFoodIngredients();
    await get.listAllFoodRecipes();
    await get.listAdminMenus();
  }
};


export default get;